import { useState } from 'react'
import { X, Sparkles, Loader2, CheckCircle2, AlertCircle, Image as ImageIcon, Layers } from 'lucide-react'
import { analyzerApi, ImageAnalysis, API_BASE } from '../services/api'

interface IntegratedCoverGenerationModalProps {
  analysis: ImageAnalysis
  userPoints: number
  onClose: () => void
  onSuccess: (assetId: string) => void
}

// Text-to-Image 모델 (프롬프트 모드)
const TEXT_TO_IMAGE_MODELS = [
  { id: 'gemini-2.5-flash-image', name: 'Gemini 2.5 Flash', cost: 39, description: '빠르고 효율적' },
  { id: 'gemini-2.0-flash-image', name: 'Gemini 2.0 Flash', cost: 39, description: '균형잡힌 성능' },
  { id: 'imagen-3', name: 'Imagen 3', cost: 30, description: '최저가, 사진같은 품질' },
  { id: 'dall-e-3', name: 'DALL-E 3', cost: 80, description: 'OpenAI 고품질' },
]

// Image-to-Image 모델 (합성 모드)
const IMAGE_TO_IMAGE_MODELS = [
  { id: 'gemini-3-pro-image-preview', name: 'Gemini 3 Pro', cost: 120, description: '4K 지원, 다중 이미지 합성' },
  { id: 'gemini-2.5-flash-image', name: 'Gemini 2.5 Flash', cost: 39, description: '빠른 합성' },
]

type GenerationMode = 'composite' | 'prompt'

type ElementType = 'background' | 'character' | 'title' | 'subtitle' | 'effect' | 'original'

const ELEMENT_LABELS: Record<ElementType, string> = {
  background: '배경',
  character: '인물',
  title: '제목',
  subtitle: '부제',
  effect: '효과',
  original: '원본 이미지'
}

export function IntegratedCoverGenerationModal({
  analysis,
  userPoints,
  onClose,
  onSuccess
}: IntegratedCoverGenerationModalProps) {
  // 모드 선택: 합성(composite) vs 통합프롬프트(prompt)
  const [mode, setMode] = useState<GenerationMode>('composite')

  const [selectedElements, setSelectedElements] = useState<ElementType[]>([])
  const [selectedModel, setSelectedModel] = useState(mode === 'composite' ? 'gemini-3-pro-image-preview' : 'gemini-2.5-flash-image')
  const [combinedPrompt, setCombinedPrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generatedAssetId, setGeneratedAssetId] = useState<string | null>(null)
  const [generationCost, setGenerationCost] = useState(0)
  const [generationProgress, setGenerationProgress] = useState(0)

  const availableModels = mode === 'composite' ? IMAGE_TO_IMAGE_MODELS : TEXT_TO_IMAGE_MODELS
  const selectedModelInfo = availableModels.find(m => m.id === selectedModel) || availableModels[0]
  const hasInsufficientPoints = userPoints < selectedModelInfo.cost

  // Helper function to generate fallback prompt from analysis object
  const generateFallbackPrompt = (analysisObj: any): string | null => {
    if (!analysisObj) return null

    // Try to construct a prompt from available fields
    const parts: string[] = []
    if (analysisObj.description) parts.push(analysisObj.description)
    if (analysisObj.setting) parts.push(analysisObj.setting)
    if (analysisObj.mood) parts.push(analysisObj.mood)
    if (analysisObj.style) parts.push(analysisObj.style)

    return parts.length > 0 ? parts.join(', ') : null
  }

  // Available elements with prompts and images
  const availableElements: Array<{ type: ElementType; prompt: string | null; imageId: string | null }> = [
    // Original asset image
    ...(analysis.asset_id ? [{
      type: 'original' as const,
      prompt: '원본 asset 이미지 (제목 텍스트를 자연스럽게 제거하고 다른 요소들과 조화롭게 합성)',
      imageId: analysis.asset_id
    }] : []),
    {
      type: 'background' as const,
      prompt: analysis.final_background_prompt ||
              analysis.background_prompt ||
              analysis.backgroundAnalysis?.prompt ||
              generateFallbackPrompt(analysis.backgroundAnalysis) ||
              null,
      imageId: analysis.generated_background_id || null
    },
    {
      type: 'character' as const,
      prompt: analysis.final_character_prompt ||
              analysis.character_prompt ||
              analysis.characterAnalysis?.prompt ||
              generateFallbackPrompt(analysis.characterAnalysis) ||
              null,
      imageId: analysis.generated_character_id || null
    },
    {
      type: 'title' as const,
      prompt: analysis.final_typography_prompt ||
              analysis.typography_prompt ||
              analysis.typographyAnalysis?.prompt ||
              generateFallbackPrompt(analysis.typographyAnalysis) ||
              null,
      imageId: analysis.generated_typography_id || null
    },
    {
      type: 'subtitle' as const,
      prompt: analysis.final_subtitle_prompt ||
              analysis.subtitle_prompt ||
              analysis.subtitleAnalysis?.prompt ||
              generateFallbackPrompt(analysis.subtitleAnalysis) ||
              null,
      imageId: analysis.generated_subtitle_id || null
    },
    {
      type: 'effect' as const,
      prompt: analysis.final_effect_prompt ||
              analysis.effect_prompt ||
              analysis.effectAnalysis?.prompt ||
              generateFallbackPrompt(analysis.effectAnalysis) ||
              null,
      imageId: analysis.generated_effect_id || null
    }
  ].filter(el => (el.prompt && el.prompt.trim().length > 0) || el.imageId !== null)

  // Generate combined prompt from selected elements
  const generateCombinedPrompt = () => {
    if (selectedElements.length === 0) {
      setError('최소 1개 이상의 요소를 선택해주세요.')
      return
    }

    const prompts = selectedElements
      .map(type => {
        const element = availableElements.find(el => el.type === type)
        if (!element || !element.prompt) return null

        // Remove unwanted directives from prompt
        let cleanPrompt = element.prompt
          // Remove background directive (중요: 배경은...)
          .replace(/중요:\s*배경[^\n]*/g, '')
          // Remove "### P 사용은" markdown headers
          .replace(/###\s*P\s*사용은[^\n]*/g, '')
          // Clean up multiple newlines
          .replace(/\n\s*\n\s*\n+/g, '\n\n')
          .trim()

        return `${ELEMENT_LABELS[type]}: ${cleanPrompt}`
      })
      .filter(Boolean)

    const combined = `Create a professional book cover combining the following elements:\n\n${prompts.join('\n\n')}\n\nCompose these elements harmoniously to create a cohesive, professional book cover design.`
    setCombinedPrompt(combined)
    setError(null)
  }

  const toggleElement = (type: ElementType) => {
    setSelectedElements(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    )
  }

  const handleGenerate = async () => {
    if (selectedElements.length === 0) {
      setError('최소 1개 이상의 요소를 선택해주세요.')
      return
    }

    // For composite mode, we need at least one element with an image
    if (mode === 'composite') {
      const selectedElementsWithImages = selectedElements.filter(type => {
        const element = availableElements.find(el => el.type === type)
        return element && element.imageId
      })

      if (selectedElementsWithImages.length === 0) {
        setError('합성 모드에서는 이미지가 있는 요소를 최소 1개 선택해야 합니다.')
        return
      }
    }

    // For prompt mode, combined prompt is required
    if (mode === 'prompt' && !combinedPrompt.trim()) {
      setError('프롬프트를 입력해주세요.')
      return
    }

    setIsGenerating(true)
    setError(null)
    setGenerationProgress(0)

    try {
      // Step 1: Start generation
      let result

      if (mode === 'composite') {
        // Composite mode: Use image-to-image API with selected element images
        result = await analyzerApi.generateCompositeCover(analysis.id, {
          elementTypes: selectedElements,
          compositionPrompt: combinedPrompt.trim() || '선택한 요소들을 조화롭게 합성하여 전문적인 책 표지를 만들어주세요.',
          model: selectedModel
        })
      } else {
        // Prompt mode: Use text-to-image API with combined prompt
        result = await analyzerApi.generateIntegratedCover(analysis.id, {
          elementTypes: selectedElements,
          customPrompt: combinedPrompt,
          model: selectedModel
        })
      }

      if (!result.success || !result.data) {
        if (result.error?.code === 'INSUFFICIENT_POINTS') {
          const details = result.error.details as any
          setError(`포인트가 부족합니다. (필요: ${details?.required}P, 보유: ${details?.current}P)`)
        } else {
          setError(result.error?.message || '표지 생성 시작에 실패했습니다.')
        }
        setIsGenerating(false)
        return
      }

      const generationId = result.data.generationId
      setGenerationProgress(10)

      // Step 2: Poll for completion
      let attempts = 0
      const maxAttempts = 60 // 2분 (2초 x 60)

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000)) // Wait 2 seconds
        attempts++

        const statusResult = await analyzerApi.getCoverGenerationStatus(analysis.id, generationId)

        if (!statusResult.success || !statusResult.data) {
          continue // Retry on error
        }

        const status = statusResult.data

        // Update progress
        if (status.progress !== undefined) {
          setGenerationProgress(status.progress)
        } else {
          // Estimate progress based on attempts
          const estimatedProgress = Math.min(10 + (attempts * 1.5), 95)
          setGenerationProgress(estimatedProgress)
        }

        if (status.status === 'completed') {
          setGeneratedAssetId(status.assetId!)
          setGenerationCost(status.cost || 0)
          setGenerationProgress(100)
          onSuccess(status.assetId!)
          setIsGenerating(false)
          return
        } else if (status.status === 'failed') {
          setError(status.errorMessage || '표지 생성에 실패했습니다.')
          setIsGenerating(false)
          return
        }
      }

      // Timeout
      setError('표지 생성 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.')
    } catch (err: any) {
      setError(err.message || '표지 생성 중 오류가 발생했습니다.')
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-neutral-900 rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-accent-primary" />
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-neutral-100">통합 표지 생성</h2>
                {/* Mode Selector */}
                <div className="inline-flex rounded-lg border border-neutral-700 p-1 gap-1">
                  <button
                    onClick={() => {
                      setMode('composite')
                      setSelectedModel('gemini-3-pro-image-preview')
                      setCombinedPrompt('')
                      setError(null)
                    }}
                    disabled={isGenerating}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                      mode === 'composite'
                        ? 'bg-accent-primary text-white'
                        : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <Layers className="w-3.5 h-3.5" />
                    합성
                  </button>
                  <button
                    onClick={() => {
                      setMode('prompt')
                      setSelectedModel('gemini-2.5-flash-image')
                      setCombinedPrompt('')
                      setError(null)
                    }}
                    disabled={isGenerating}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                      mode === 'prompt'
                        ? 'bg-accent-primary text-white'
                        : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    프롬프트
                  </button>
                </div>
              </div>
              <p className="text-sm text-neutral-400 mt-1">
                {mode === 'composite'
                  ? '선택한 요소 이미지들을 조화롭게 합성하여 하나의 표지를 만듭니다'
                  : '선택한 요소들의 프롬프트를 조합하여 새로운 표지를 생성합니다'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-neutral-400">보유 포인트</p>
              <p className="text-lg font-bold text-accent-primary">{userPoints.toLocaleString()}P</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-neutral-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Element Selection */}
          <div className="w-1/3 border-r border-neutral-800 overflow-y-auto p-6">
            <h3 className="text-sm font-medium text-neutral-200 mb-4">
              요소 선택
              {mode === 'composite' && (
                <span className="text-xs text-neutral-500 ml-2">(이미지가 있는 요소만 합성 가능)</span>
              )}
            </h3>
            <div className="space-y-2">
              {availableElements.map(element => {
                const hasImage = element.imageId !== null
                const isDisabledInCompositeMode = mode === 'composite' && !hasImage

                return (
                  <button
                    key={element.type}
                    onClick={() => toggleElement(element.type)}
                    disabled={isGenerating || isDisabledInCompositeMode}
                    className={`
                      w-full text-left p-4 rounded-lg border-2 transition-all
                      ${selectedElements.includes(element.type)
                        ? 'border-accent-primary bg-accent-primary/10'
                        : 'border-neutral-700 hover:border-neutral-600 bg-neutral-800'
                      }
                      ${isDisabledInCompositeMode ? 'opacity-40 cursor-not-allowed' : ''}
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    <div className="flex items-start gap-3">
                      {/* Element Image (if available) */}
                      {hasImage && (
                        <div className="flex-shrink-0 w-16 h-16 rounded border border-neutral-700 overflow-hidden bg-neutral-900">
                          <img
                            src={`${API_BASE}/assets/${element.imageId}/thumbnail`}
                            alt={ELEMENT_LABELS[element.type]}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}

                      {/* Element Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-neutral-100">{ELEMENT_LABELS[element.type]}</span>
                            {hasImage && (
                              <ImageIcon className="w-3.5 h-3.5 text-accent-gold" />
                            )}
                          </div>
                          {selectedElements.includes(element.type) && (
                            <CheckCircle2 className="w-5 h-5 text-accent-primary flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-neutral-400 line-clamp-2">
                          {element.prompt || (hasImage ? '생성된 이미지' : '요소 없음')}
                        </p>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Right Panel - Prompt & Settings */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Model Selection */}
            <div className="bg-neutral-800 rounded-lg p-4 space-y-3">
              <label className="block text-sm font-medium text-neutral-200">
                AI 모델 선택
                <span className="text-xs text-neutral-400 ml-2">
                  ({mode === 'composite' ? '이미지 합성' : '텍스트 생성'})
                </span>
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={isGenerating}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-primary disabled:opacity-50"
              >
                {availableModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name} - {model.cost}P ({model.description})
                  </option>
                ))}
              </select>
              {hasInsufficientPoints && (
                <div className="flex items-center gap-2 text-accent-error text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>포인트가 부족합니다. (필요: {selectedModelInfo.cost}P)</span>
                </div>
              )}
            </div>

            {/* Prompt/Instructions Section */}
            <div className="bg-neutral-800 rounded-lg p-4 space-y-3">
              <label className="block text-sm font-medium text-neutral-200">
                {mode === 'composite' ? '합성 지시사항' : '통합 프롬프트'}
                <span className="text-xs text-neutral-400 ml-2">
                  ({selectedElements.length}개 요소 선택됨)
                </span>
              </label>

              {mode === 'prompt' ? (
                // Prompt mode: Generate combined prompt from element prompts
                <div className="space-y-3">
                  <button
                    onClick={generateCombinedPrompt}
                    disabled={selectedElements.length === 0 || isGenerating}
                    className="w-full px-4 py-3 bg-neutral-700 hover:bg-neutral-600 disabled:bg-neutral-800 text-neutral-100 disabled:text-neutral-500 rounded-lg transition-colors font-medium disabled:cursor-not-allowed"
                  >
                    선택한 요소로 프롬프트 생성 ({selectedElements.length}개)
                  </button>
                  <textarea
                    value={combinedPrompt}
                    onChange={(e) => setCombinedPrompt(e.target.value)}
                    placeholder="위 버튼을 클릭하여 프롬프트를 생성한 후, 자유롭게 수정할 수 있습니다."
                    disabled={isGenerating}
                    rows={12}
                    className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-accent-primary disabled:opacity-50 resize-none"
                  />
                </div>
              ) : (
                // Composite mode: Simple composition instructions
                <div className="space-y-3">
                  <p className="text-xs text-neutral-400">
                    선택한 요소 이미지들을 어떻게 합성할지 지시사항을 입력하세요. (선택사항)
                  </p>
                  <textarea
                    value={combinedPrompt}
                    onChange={(e) => setCombinedPrompt(e.target.value)}
                    placeholder="예: 인물을 배경 앞에 배치하고, 제목을 상단에 크게 표시해주세요. (비워두면 AI가 자동으로 조화롭게 합성합니다)"
                    disabled={isGenerating}
                    rows={6}
                    className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-md text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-accent-primary disabled:opacity-50 resize-none"
                  />
                  {selectedElements.length > 0 && (
                    <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-3">
                      <p className="text-xs font-medium text-neutral-300 mb-2">📋 이미지 합성 순서:</p>
                      <div className="text-xs text-neutral-400 space-y-1">
                        {(() => {
                          // Reorder to place 'original' first, matching backend logic
                          const orderedElements = [...selectedElements].sort((a, b) => {
                            if (a === 'original') return -1
                            if (b === 'original') return 1
                            return 0
                          })

                          const elementDescriptions: Record<ElementType, string> = {
                            original: '원본 표지 이미지 - 제목 텍스트는 제거하고 레이아웃 구성을 참고',
                            background: '배경 이미지',
                            character: '인물 이미지',
                            title: '제목 타이포그래피 이미지',
                            subtitle: '부제 이미지',
                            effect: '효과 이미지'
                          }

                          return orderedElements.map((element, index) => (
                            <div key={element}>
                              <strong className="text-accent-primary">{index + 1}번째 이미지:</strong>{' '}
                              <span className={element === 'original' ? 'text-accent-gold font-medium' : 'text-neutral-300'}>
                                {elementDescriptions[element]}
                              </span>
                            </div>
                          ))
                        })()}
                      </div>
                      <p className="text-xs text-neutral-500 mt-2">
                        💡 프롬프트에서 "1번째 이미지", "2번째 이미지" 등으로 특정 이미지를 지정할 수 있습니다.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-accent-error/10 border border-accent-error rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-accent-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-accent-error">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {generatedAssetId && (
              <div className="bg-accent-success/10 border border-accent-success rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent-success flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-accent-success font-medium">통합 표지 생성 완료!</p>
                    <p className="text-xs text-neutral-400 mt-1">
                      {generationCost.toFixed(3)}$ 사용
                    </p>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-neutral-700">
                  <img
                    src={`${API_BASE}/assets/${generatedAssetId}/image`}
                    alt="Generated integrated cover"
                    className="w-full"
                  />
                </div>
              </div>
            )}

            {/* Generate Button */}
            {!generatedAssetId && (
              <>
                <button
                  onClick={handleGenerate}
                  disabled={
                    selectedElements.length === 0 ||
                    (mode === 'prompt' && !combinedPrompt.trim()) ||
                    isGenerating ||
                    hasInsufficientPoints
                  }
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-accent-primary to-accent-gold text-white text-lg font-bold rounded-lg hover:from-accent-primary/90 hover:to-accent-gold/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl disabled:shadow-none"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      <span>표지 생성 중... {Math.round(generationProgress)}%</span>
                    </>
                  ) : (
                    <>
                      {mode === 'composite' ? <Layers className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
                      <span>{mode === 'composite' ? '이미지 합성' : '통합 표지 생성'} ({selectedModelInfo.cost}P 사용)</span>
                    </>
                  )}
                </button>

                {/* Progress Bar */}
                {isGenerating && (
                  <div className="w-full bg-neutral-700 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-accent-primary to-accent-gold transition-all duration-300"
                      style={{ width: `${generationProgress}%` }}
                    />
                  </div>
                )}
              </>
            )}

            {/* Success Actions */}
            {generatedAssetId && (
              <div className="flex gap-3">
                <button
                  onClick={() => window.open(`/assets?highlight=${generatedAssetId}`, '_blank')}
                  className="flex-1 px-6 py-3 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
                >
                  Assets에서 보기
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-3 bg-accent-primary hover:bg-accent-primary/90 text-white rounded-lg transition-colors"
                >
                  닫기
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
